import { useCallback, useState, type MouseEvent } from 'react';
import {
  Box,
  IconButton,
  Menu,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import SettingsIcon from '@material-ui/icons/Settings';
import { TechDocsSearch } from '@backstage/plugin-techdocs';
import {
  TechDocsAddonLocations,
  useTechDocsAddons,
  useTechDocsReaderPage,
} from '@backstage/plugin-techdocs-react';
import { TECHDOCS_MOBILE_MAX_WIDTH } from './techDocsBreakpoints';

/** Matches MkDocs / TechDocs primary + secondary sidebar width (16rem each). */
const TECHDOCS_SIDEBAR_WIDTH = '16rem';

/** Class used by TechDocsNavScrollFix to pin sidebars to this row. */
export const TECHDOCS_SEARCH_TOOLBAR_CLASS = 'techdocs-search-toolbar';

const useStyles = makeStyles(theme => ({
  root: {
    // Occupy the Page subheader row (replaces the empty settings-only toolbar).
    gridArea: 'pageSubheader',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: theme.spacing(1),
    // Tight padding so the left nav (aligned to this row) has no empty band.
    padding: theme.spacing(0, 2, 0.5),
    boxSizing: 'border-box' as const,
    minHeight: 'auto',
    // Same as upstream TechDocsReaderPageSubheader: hide chrome when printing
    // so only the article content is printed.
    '@media print': {
      display: 'none',
    },
  },
  navSpacer: {
    flex: `0 0 ${TECHDOCS_SIDEBAR_WIDTH}`,
    // Hide spacers at the MkDocs / Backstage mobile max-width (76.1875em),
    // which pairs with the desktop min-width of 76.25em.
    [`@media screen and (max-width: ${TECHDOCS_MOBILE_MAX_WIDTH})`]: {
      display: 'none',
    },
  },
  search: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    flex: '0 0 auto',
  },
  tocSpacer: {
    flex: `0 0 ${TECHDOCS_SIDEBAR_WIDTH}`,
    [`@media screen and (max-width: ${TECHDOCS_MOBILE_MAX_WIDTH})`]: {
      display: 'none',
    },
  },
}));

/**
 * Search + settings on one row, with side spacers so the field lines up with
 * the article column and the left nav can sit in the same horizontal band.
 */
export const CustomTechDocsSearchToolbar = () => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const {
    entityRef,
    entityMetadata: { value: entityMetadata, loading: entityMetadataLoading },
  } = useTechDocsReaderPage();
  const addons = useTechDocsAddons();
  const subheaderAddons = addons.renderComponentsByLocation(
    TechDocsAddonLocations.Subheader,
  );
  const settingsAddons = addons.renderComponentsByLocation(
    TechDocsAddonLocations.Settings,
  );

  const handleOpenSettings = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);
  const handleCloseSettings = useCallback(() => {
    setAnchorEl(null);
  }, []);

  if (entityMetadataLoading === false && !entityMetadata) {
    return null;
  }

  return (
    <Box className={`${TECHDOCS_SEARCH_TOOLBAR_CLASS} ${classes.root}`}>
      <div className={classes.navSpacer} aria-hidden />
      <div className={classes.search}>
        <TechDocsSearch
          entityId={entityRef}
          entityTitle={entityMetadata?.metadata?.title}
        />
      </div>
      <div className={classes.actions}>
        {subheaderAddons}
        {settingsAddons ? (
          <>
            <Tooltip title="Settings">
              <IconButton
                aria-controls="tech-docs-reader-page-settings"
                aria-haspopup="true"
                size="small"
                onClick={handleOpenSettings}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
            <Menu
              id="tech-docs-reader-page-settings"
              getContentAnchorEl={null}
              anchorEl={anchorEl}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              open={Boolean(anchorEl)}
              onClose={handleCloseSettings}
              keepMounted
            >
              <div>{settingsAddons}</div>
            </Menu>
          </>
        ) : null}
      </div>
      <div className={classes.tocSpacer} aria-hidden />
    </Box>
  );
};
